/**
 * StatisticalAnalysisSummary Component
 *
 * Displays AI-generated statistical analysis based on the framework.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, RefreshCw, ChevronDown, ChevronUp, ShieldAlert, Scale } from 'lucide-react';
import { useDuckDB } from '../context/LazyDuckDBContext';
import { analyzeMunicipalityKPIs } from '../services/statisticalAnalysisService';
import type { StatisticalAnalysisInput } from '../services/statisticalAnalysisFramework';
import { generateNarrative } from '../services/llmService';
import LLMSettings from './LLMSettings';
import Portal from './Portal';
import MarkdownText from './MarkdownText';

interface StatisticalAnalysisSummaryProps {
  municipalityId: string;
  municipalityName: string;
  year: number;
  kpiIds: string[];
  onAnalysisReady?: (analysis: StatisticalAnalysisInput) => void;
}

export const StatisticalAnalysisSummary: React.FC<StatisticalAnalysisSummaryProps> = ({
  municipalityId,
  municipalityName,
  year,
  kpiIds,
  onAnalysisReady
}) => {
  const { query, ready } = useDuckDB();
  const [analysis, setAnalysis] = useState<StatisticalAnalysisInput | null>(null);
  const [loading, setLoading] = useState(true);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [narrative, setNarrative] = useState<string>('');
  const [showSettings, setShowSettings] = useState(false);
  const [usingTemplate, setUsingTemplate] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const isLoadingRef = React.useRef(false);

  const regenerateNarrative = useCallback(async () => {
    if (!analysis) return;
    setRegenerating(true);
    setNarrativeLoading(true);
    try {
      const llmNarrative = await generateNarrative(analysis);
      if (llmNarrative) {
        setNarrative(llmNarrative);
        setUsingTemplate(false);
      } else {
        setNarrative(generateNarrativeTemplate(analysis));
        setUsingTemplate(true);
      }
    } catch (llmError) {
      setNarrative(generateNarrativeTemplate(analysis));
      setUsingTemplate(true);
    } finally {
      setRegenerating(false);
      setNarrativeLoading(false);
    }
  }, [analysis]);

  const handleApiKeySaved = useCallback(() => {
    regenerateNarrative();
  }, [regenerateNarrative]);

  useEffect(() => {
    if (showSettings) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showSettings]);

  useEffect(() => {
    const loadAnalysis = async () => {
      if (!ready || !query || isLoadingRef.current) return;
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      try {
        const input = await analyzeMunicipalityKPIs(query, municipalityId, municipalityName, year, kpiIds);
        if (!input) {
          setAnalysis(null);
          setLoading(false);
          isLoadingRef.current = false;
          return;
        }
        
        setAnalysis(input);
        if (onAnalysisReady) onAnalysisReady(input);

        setNarrativeLoading(true);
        const llmNarrative = await generateNarrative(input);
        if (llmNarrative) {
          setNarrative(llmNarrative);
          setUsingTemplate(false);
        } else {
          setNarrative(generateNarrativeTemplate(input));
          setUsingTemplate(true);
        }
      } catch (err: any) {
        setError(err.message || 'Ett fel uppstod vid analys');
      } finally {
        setLoading(false);
        setNarrativeLoading(false);
        isLoadingRef.current = false;
      }
    };
    loadAnalysis();
  }, [municipalityId, year, ready, query, kpiIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2.5rem] p-6 animate-pulse">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-800" />
          <div className="h-4 w-32 bg-slate-100 dark:bg-slate-800 rounded-full" />
        </div>
        <div className="space-y-3">
          <div className="h-4 w-full bg-slate-50 dark:bg-slate-800/50 rounded-full" />
          <div className="h-4 w-3/4 bg-slate-50 dark:bg-slate-800/50 rounded-full" />
        </div>
      </div>
    );
  }

  if (error || !analysis || !narrative) {
    return null;
  }

  const narrativeContent = narrative.trim();
  const splitIndex = narrativeContent.search(/\n\n|\n[*|-]/);
  let headline: string;
  let remainingNarrative: string;

  if (splitIndex !== -1) {
    headline = narrativeContent.slice(0, splitIndex).trim();
    remainingNarrative = narrativeContent.slice(splitIndex).trim();
  } else {
    const lines = narrativeContent.split('\n');
    if (lines[0].startsWith('**') && (lines[0].endsWith('**') || lines[0].includes('**'))) {
      headline = lines[0];
      remainingNarrative = lines.slice(1).join('\n').trim();
    } else {
      headline = narrativeContent;
      remainingNarrative = '';
    }
  }

  return (
    <>
      <div className={`overflow-hidden transition-all duration-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2.5rem] shadow-lg dark:shadow-2xl ${isExpanded ? 'p-6' : 'p-4'}`}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full text-left group"
          aria-expanded={isExpanded}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
                Statistisk Analys
              </span>
              <span
                className="text-[9px] font-bold text-slate-300 dark:text-slate-600 border border-slate-200 dark:border-slate-800 px-1.5 py-0.5 rounded cursor-help uppercase tracking-widest"
                title="Automatiskt genererad analys"
              >
                Beta
              </span>
            </div>
            <div className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-300" /> : <ChevronDown className="w-4 h-4 text-slate-300" />}
            </div>
          </div>

          {!isExpanded && (
            <div className="space-y-4">
              <div className="text-xl font-bold text-slate-900 dark:text-white leading-tight tracking-tight">
                <MarkdownText text={headline} inline />
              </div>
              <div className="flex flex-wrap gap-4 pt-1 border-t border-slate-50 dark:border-slate-800/50">
                <StatusIndicator label="Trend" value={getTrendLabel(analysis.overallAssessment.trend)} sentiment={analysis.overallAssessment.trend.includes('improvement') ? 'good' : analysis.overallAssessment.trend.includes('decline') ? 'bad' : 'warn'} icon={<TrendingUp />} />
                <StatusIndicator label="Risk" value={getRiskLabel(analysis.overallAssessment.systemRisk)} sentiment={analysis.overallAssessment.systemRisk === 'high' ? 'bad' : analysis.overallAssessment.systemRisk === 'moderate' ? 'warn' : 'good'} icon={<ShieldAlert />} />
                <StatusIndicator label="Fokus" value={getEquityLabel(analysis.overallAssessment.equityLevel)} sentiment={analysis.overallAssessment.equityLevel === 'low' ? 'bad' : analysis.overallAssessment.equityLevel === 'moderate' ? 'warn' : 'good'} icon={<Scale />} />
              </div>
            </div>
          )}
        </button>

        {isExpanded && (
          <div className="mt-8 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <StatusIcon status={analysis.overallAssessment.status} />
                  <h4 className="text-base font-bold text-slate-900 dark:text-white">
                    {getStatusLabel(analysis.overallAssessment.status)}
                  </h4>
                </div>
                <div className="text-xl font-bold text-slate-900 dark:text-white leading-tight tracking-tight">
                  <MarkdownText text={headline} inline />
                </div>
                <div className="prose prose-slate dark:prose-invert max-w-none">
                  {narrativeLoading ? (
                    <div className="py-8 border-y border-slate-50 dark:border-slate-800/50 flex items-center gap-3 text-slate-400">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span className="text-sm font-medium">Uppdaterar analys...</span>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {remainingNarrative ? (
                        <MarkdownText text={remainingNarrative} className="text-base text-slate-600 dark:text-slate-400 leading-relaxed" />
                      ) : (
                        <p className="text-sm italic text-slate-400">Ingen ytterligare beskrivning.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t border-slate-50 dark:border-slate-800/50">
                <MetricSection title="Trend" value={getTrendLabel(analysis.overallAssessment.trend)} sentiment={analysis.overallAssessment.trend.includes('improvement') ? 'positive' : analysis.overallAssessment.trend.includes('decline') ? 'negative' : 'neutral'} />
                <MetricSection title="Systemrisk" value={getRiskLabel(analysis.overallAssessment.systemRisk)} sentiment={analysis.overallAssessment.systemRisk === 'high' ? 'negative' : analysis.overallAssessment.systemRisk === 'moderate' ? 'warning' : 'positive'} />
                <MetricSection title="Likvärdighet" value={getEquityLabel(analysis.overallAssessment.equityLevel)} sentiment={analysis.overallAssessment.equityLevel === 'low' ? 'negative' : analysis.overallAssessment.equityLevel === 'moderate' ? 'warning' : 'positive'} />
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 mt-8 border-t border-slate-50 dark:border-slate-800/50">
                <div className="flex items-center gap-4">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Senast analyserad {analysis.meta.analysisDate}</span>
                  {usingTemplate && (
                    <span className="text-[10px] font-bold text-blue-500/60 uppercase tracking-widest border border-blue-500/20 px-1.5 py-0.5 rounded">Mallbaserad</span>
                  )}
                </div>
                <div className="flex items-center gap-6">
                  <button onClick={() => setShowSettings(true)} className="text-[10px] font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors uppercase tracking-widest">Inställningar</button>
                  <button onClick={regenerateNarrative} disabled={regenerating} className="flex items-center gap-2 text-[10px] font-bold text-slate-900 dark:text-white hover:opacity-70 transition-opacity uppercase tracking-widest disabled:opacity-30">
                    <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? 'animate-spin' : ''}`} />Regenerera
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showSettings && (
        <Portal>
          <div className="fixed inset-0 z-[10000]">
            <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm pointer-events-auto" onClick={() => setShowSettings(false)} />
            <div className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none">
              <div className="pointer-events-auto w-full max-w-2xl">
                <LLMSettings onClose={() => setShowSettings(false)} onApiKeySaved={handleApiKeySaved} />
              </div>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
};

// --- HELPER COMPONENTS ---

function MetricSection({ title, value, sentiment }: { title: string, value: string, sentiment: 'positive' | 'negative' | 'neutral' | 'warning' }) {
  const colors = { positive: 'text-emerald-500', negative: 'text-rose-500', warning: 'text-amber-500', neutral: 'text-slate-400' };
  return (
    <div className="space-y-1">
      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{title}</div>
      <div className={`text-sm font-bold ${colors[sentiment]}`}>{value}</div>
    </div>
  );
}

function StatusIndicator({ label, value, sentiment, icon }: { label: string, value: string, sentiment: 'good' | 'warn' | 'bad', icon: React.ReactNode }) {
  const colors = { good: 'text-emerald-500', warn: 'text-amber-500', bad: 'text-rose-500' };
  return (
    <div className="flex items-center gap-1.5 py-0.5">
      <div className={colors[sentiment]}>{React.isValidElement(icon) ? React.cloneElement(icon as React.ReactElement<any>, { size: 12, strokeWidth: 3 }) : icon}</div>
      <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400"><span className="opacity-50">{label}:</span> {value}</span>
    </div>
  );
}

function StatusIcon({ status }: { status: string }) {
  const colors = {
    well_above: 'bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.4)]',
    above: 'bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.3)]',
    at_level: 'bg-slate-400 shadow-[0_0_10px_rgba(148,163,184,0.2)]',
    below: 'bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.3)]',
    well_below: 'bg-rose-500 shadow-[0_0_15px_rgba(244,63,94,0.4)]'
  };
  return <div className={`w-3 h-3 rounded-full ${colors[status as keyof typeof colors] || 'bg-slate-400'}`} />;
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = { well_above: 'Väl över riket', above: 'Över riket', at_level: 'I nivå med riket', below: 'Under riket', well_below: 'Väl under riket' };
  return labels[status] || status;
}

function getTrendLabel(trend: string): string {
  const labels: Record<string, string> = { strong_improvement: 'Starkt uppåt', improvement: 'Förbättras', stable: 'Stabil', decline: 'Nedåtgående', strong_decline: 'Kraftigt ned' };
  return labels[trend] || trend;
}

function getRiskLabel(level: string): string {
  const labels: Record<string, string> = { low: 'Låg', moderate: 'Måttlig', high: 'Hög' };
  return labels[level] || level;
}

function getEquityLabel(level: string): string {
  const labels: Record<string, string> = { high: 'Hög', moderate: 'Måttlig', low: 'Låg' };
  return labels[level] || level;
}

function generateNarrativeTemplate(analysis: StatisticalAnalysisInput): string {
  const { overallAssessment, keyFindings, insights } = analysis;
  let narrative = '';
  if (overallAssessment.status === 'well_above' || overallAssessment.status === 'above') {
    narrative += `${analysis.meta.municipalityName} uppvisar en stark position med resultat som ligger sammantaget över riksgenomsnittet.`;
  } else if (overallAssessment.status === 'at_level') {
    narrative += `${analysis.meta.municipalityName}s resultat följer riksnivån med en stabil och förväntad leverans.`;
  } else {
    narrative += `${analysis.meta.municipalityName}s resultat understiger riksgenomsnittet, vilket indikerar behov av riktade resursförstärkningar.`;
  }
  narrative += '\n\n';
  if (keyFindings.strengths.length > 0) {
    narrative += `**Viktigaste observationer:**\n`;
    keyFindings.strengths.slice(0, 2).forEach(s => { narrative += `* Positiv avvikelse inom ${s.kpiName.toLowerCase()}.\n`; });
  }
  if (insights.trendWarnings.length > 0) {
    narrative += `* ${insights.trendWarnings[0]}.\n`;
  }
  narrative += '\n';
  if (analysis.focusAreas.length > 0) {
    narrative += `**Prioriterade åtgärder:**\n`;
    narrative += `Nuvarande data pekar på ${analysis.focusAreas[0].area.toLowerCase()} som det mest kritiska utvecklingsområdet.`;
  } else {
    narrative += `**Nästa steg:**\nFölj upp utvecklingen i nästa mätperiod.`;
  }
  return narrative.trim();
}

export default StatisticalAnalysisSummary;
